import React, { useEffect, createContext } from 'react';
import { useAtom } from 'jotai';
import hotkeys from 'hotkeys-js';
import {
  historyAtom,
  cursorAtom,
  getHistoryNavigationFlag,
  setHistoryNavigationFlag,
  moveCursorAtom,
  selectedNodePropsAtom
} from '@src/atoms';
import { type SerializedHashTable } from '@src/scenario-editor/utils/serializer';

export interface HistoryContextValue {
  history: SerializedHashTable[];
  cursor: number;
  historyNavigationFlag: boolean;
}

const HistoryContext = createContext<HistoryContextValue>({
  history: [],
  cursor: -1,
  historyNavigationFlag: false,
});

interface HistoryProviderProps {
  children: React.ReactNode;
}
const HistoryProvider = ({ children }: HistoryProviderProps) => {
  const [history] = useAtom(historyAtom);
  const [cursor] = useAtom(cursorAtom);
  const historyNavigationFlag = getHistoryNavigationFlag();
  const [, moveCursor] = useAtom(moveCursorAtom);
  const [, setSelectedNodeProps] = useAtom(selectedNodePropsAtom);

  useEffect(() => {
    const handleUndo = () => {
      setSelectedNodeProps(null);
      setHistoryNavigationFlag(true);
      moveCursor(-1);
      setTimeout(() => {
        setHistoryNavigationFlag(false);
      }, 1000);
    }
    const handleRedo = () => {
      setSelectedNodeProps(null);
      setHistoryNavigationFlag(true);
      moveCursor(1);
      setTimeout(() => {
        setHistoryNavigationFlag(false);
      }, 1000);
    }

    hotkeys('ctrl+z, command+z', handleUndo);
    hotkeys('ctrl+shift+z, command+shift+z', handleRedo);

    return () => {
      hotkeys.unbind('ctrl+z, command+z', handleUndo);
      hotkeys.unbind('ctrl+shift+z, command+shift+z', handleRedo);
    };
  }, []);

  return (
    <HistoryContext.Provider value={{ history, cursor, historyNavigationFlag }}>
      {children}
    </HistoryContext.Provider>
  );
};

export { HistoryProvider, HistoryContext };
